import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { LoginRequest } from '@/core/auth/types';
import { servicesContainer } from '@/core/di/services-container';
import { appConfig } from '@/core/config';
import { AuthLogger } from '@/core/auth/auth-logger';

interface AuthStore {
  // State
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  isDistributor: boolean;
  isWaitingForIframeToken: boolean;
  error: string | null;
  token: string | null;
  operatorId: string | null;
  distributorId: string | null;
  accountToken: string | null;
  accountId: string | null;
  
  // Actions
  initialize: () => Promise<void>;
  login: (credentials: LoginRequest) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  setWaitingForIframeToken: (waiting: boolean) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      isDistributor: false,
      isWaitingForIframeToken: false,
      error: null,
      token: null,
      operatorId: null,
      distributorId: null,
      accountToken: null,
      accountId: null,

      /**
       * Initialize authentication
       * 
       * Simplified flow:
       * - Production: Try cortex_session -> if fails -> show login page
       * - Dev/QA: Show login page (no auto-login)
       */
      initialize: async () => {
        // Prevent multiple initializations
        if (get().isInitialized) {
          return;
        }

        set({ isLoading: true, error: null });

        const isProduction = appConfig.isProduction();
        const env = appConfig.getEnvironment();
        
        AuthLogger.logEnvironment(env);
        AuthLogger.logInitStart();

        try {
          const services = servicesContainer.getServices();

          // Execute initialization use case with persisted state
          const currentState = get();
          const result = await services.initializeAuthUseCase.execute(
            isProduction,
            currentState.token,
            currentState.operatorId,
            currentState.distributorId,
            currentState.isDistributor,
            currentState.accountToken,
            currentState.accountId
          );

          if (result.success && result.tokenInfo) {
            // Success - user is authenticated
            const logMethod = result.reason === 'stored_tokens' ? 'cortex_session' : result.reason as 'cortex_session' | 'login';
            AuthLogger.logAuthResult(true, logMethod);
            AuthLogger.logUserInfo(
              result.tokenInfo.operatorId,
              result.tokenInfo.distributorId || null,
              result.isDistributor
            );

            set({
              isAuthenticated: true,
              isLoading: false,
              isInitialized: true,
              token: result.tokenInfo.appToken,
              operatorId: result.tokenInfo.operatorId,
              distributorId: result.tokenInfo.distributorId || null,
              isDistributor: result.isDistributor,
              accountToken: result.tokenInfo.accountToken,
              accountId: result.tokenInfo.accountId,
              error: null,
            });
          } else {
            // Not authenticated - show login page
            AuthLogger.logAuthResult(false, 'none');

            set({
              isAuthenticated: false,
              isLoading: false,
              isInitialized: true,
              token: null,
              operatorId: null,
              distributorId: null,
              isDistributor: false,
              error: null,
            });
          }

          AuthLogger.logInitComplete();
        } catch (error) {
          AuthLogger.logError('Initialization failed', error);
          
          set({
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
            error: error instanceof Error ? error.message : 'Initialization failed',
          });

          AuthLogger.logInitComplete();
        }
      },

      /**
       * Login with credentials
       */
      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null });
        
        AuthLogger.logLoginAttempt(credentials.username);

        try {
          const services = servicesContainer.getServices();
          const result = await services.loginUseCase.execute(credentials);

           if (result.success && result.tokenInfo) {
            AuthLogger.logAuthResult(true, 'login');
            AuthLogger.logUserInfo(
              result.tokenInfo.operatorId,
              result.tokenInfo.distributorId || null,
              result.isDistributor
            );

            set({
              isAuthenticated: true,
              isLoading: false,
              isInitialized: true,
              token: result.tokenInfo.appToken,
              operatorId: result.tokenInfo.operatorId,
              distributorId: result.tokenInfo.distributorId || null,
              isDistributor: result.isDistributor,
              accountToken: result.tokenInfo.accountToken,
              accountId: result.tokenInfo.accountId,
              error: null,
            });
          } else {
            AuthLogger.logError('Login failed', result.error);
            
            set({
              isLoading: false,
              error: result.error || 'Login failed',
              isAuthenticated: false,
            });
          }
        } catch (error) {
          AuthLogger.logError('Login exception', error);
          
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Login failed',
            isAuthenticated: false,
          });
        }
      },

      /**
       * Login with external token (from postMessage)
       * Automatically authenticates without showing login screen
       */
      loginWithToken: async (token: string) => {
        set({ isLoading: true, error: null, isWaitingForIframeToken: false });
        
        AuthLogger.logInfo('🔐 Automatic login with external token');

        try {
          const services = servicesContainer.getServices();
          const result = await services.loginWithTokenUseCase.execute(token);

           if (result.success && result.tokenInfo) {
            AuthLogger.logAuthResult(true, 'cortex_session');
            AuthLogger.logUserInfo(
              result.tokenInfo.operatorId,
              result.tokenInfo.distributorId || null,
              result.isDistributor
            );

            set({
              isAuthenticated: true,
              isLoading: false,
              isInitialized: true,
              token: result.tokenInfo.appToken,
              operatorId: result.tokenInfo.operatorId,
              distributorId: result.tokenInfo.distributorId || null,
              isDistributor: result.isDistributor,
              accountToken: result.tokenInfo.accountToken,
              accountId: result.tokenInfo.accountId,
              error: null,
            });
          } else {
            AuthLogger.logError('Token login failed', result.error);
            
            set({
              isLoading: false,
              error: result.error || 'Token authentication failed',
              isAuthenticated: false,
            });
          }
        } catch (error) {
          AuthLogger.logError('Token login exception', error);
          
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Token authentication failed',
            isAuthenticated: false,
          });
        }
      },

      /**
       * Logout
       */
      logout: () => {
        AuthLogger.logLogout();
        
        const services = servicesContainer.getServices();
        services.logoutUseCase.execute();
        
        set({
          isAuthenticated: false,
          token: null,
          operatorId: null,
          distributorId: null,
          isDistributor: false,
          accountToken: null,
          accountId: null,
          error: null,
        });
      },

      /**
       * Clear error
       */
      clearError: () => set({ error: null }),

      /**
       * Set waiting for iframe token state
       */
      setWaitingForIframeToken: (waiting: boolean) => set({ isWaitingForIframeToken: waiting }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        operatorId: state.operatorId,
        distributorId: state.distributorId,
        isDistributor: state.isDistributor,
        accountToken: state.accountToken,
        accountId: state.accountId,
        // DO NOT persist isAuthenticated - it's derived from token validity
        // Persisting it causes stale auth state on page reload when token expires
      }),
      onRehydrateStorage: () => () => {
        // After rehydration, isAuthenticated will be set to false by default
        // and will be updated to true by initialize() if token is valid
      }
    }
  )
);